import { Clear } from '@mui/icons-material'
import { CssBaseline, IconButton } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { AuthProvider, FedExAddressProvider, LayoutProvider, ThemeProvider, useAuth } from 'context'
import { VehicleDto } from 'dtos'
import { SnackbarProvider, closeSnackbar } from 'notistack'
import { Blank, ForgotPassword, ResetPassword, SignIn } from 'pages'
import ClusterShippers from 'pages/clusterShippers'
import ClusterShippersId from 'pages/clusterShippers/id'
import ClusterShipperNeedsClusterDeliveredToThirdPartyConversionCompany from 'pages/clusterShippers/id/needsClusterDeliveredToThirdPartyConversionCompany'
import Companies from 'pages/companies'
import CompaniesId from 'pages/companies/id'
import Conformity from 'pages/conformity'
import Damage from 'pages/damage'
import AddDamage from 'pages/damage/id'
import InboundTripsListCard from 'pages/inboundTrips'
import InboundTripsDetails from 'pages/inboundTrips/id'
import InboundTripsGuidPAPSGuid from 'pages/inboundTrips/id/papsID'
import Invoicing from 'pages/invoicing'
import MoveVehicle from 'pages/moveVehicle'
import NeedsDriversLicensePhotoUploaded from 'pages/needsDriversLicensePhotoUploaded'
import NeedsToBeMovedToStaging from 'pages/needsToBeMovedToStaging'
import OCR from 'pages/ocr'
import OCRDocumentGuidIndex from 'pages/ocr/documentGuid'
import OutboundTripsListCard from 'pages/outboundTrips'
import OutboundTripsDetails from 'pages/outboundTrips/id'
import OutboundTripNeedsDriversLicensePhotoUploadedNEW from 'pages/outboundTrips/needsDriversLicensePhotoUploaded'
import People from 'pages/people'
import PeopleId from 'pages/people/id'
import Reports from 'pages/reports'
import FinalSettings from 'pages/settings/id'
import MichiganOutgoingTitlesId from 'pages/titles/michiganOutgoingTitles/id'
import MichiganOutgoingTitlesListCard from 'pages/titles/michiganOutgoingTitles/index'
import MichiganSOSBundlesDetails from 'pages/titles/michiganSoSBundles/id'
import MichiganSOSBundlesListCard from 'pages/titles/michiganSoSBundles/index'
import NeedsToBeTakenToSecretaryOfState from 'pages/titles/michiganSoSBundles/needsToBeTakenToSecretaryOfState'
import MichiganTitlesListCard from 'pages/titles/michiganTitles/index'
import OhioBuildABoxes from 'pages/titles/ohioBuildABoxes'
import OhioBuildABox from 'pages/titles/ohioBuildABoxes/ohioBuildABoxGuid'
import OhioOutgoingTitlesId from 'pages/titles/ohioOutgoingTitles/id'
import OhioOutgoingTitlesListCard from 'pages/titles/ohioOutgoingTitles/index'
import OhioTitlesListCard from 'pages/titles/ohioTitles/index'
import ToDoList from 'pages/todolist/index'
import NeedsClusterRemovedScanClusterBarcodeIndex from 'pages/todolist/NeedsClusterRemovedScanClusterBarcodeIndex'
import VehiclesId from 'pages/vehicles/id'
import VehicleCheckInNEW from 'pages/vehicles/id/checkIn'
import VehicleClusterReinstallNEW from 'pages/vehicles/id/clusterReinstall'
import VehicleClusterRemovedNEW from 'pages/vehicles/id/clusterRemoved'
import DamageDetailsIndex from 'pages/vehicles/id/damage'
import VehicleNeedsClusterConversionInHouseNEW from 'pages/vehicles/id/needsClusterConversionInHouse'
import VehicleNeedsInHouseAdditionalProgrammingNEW from 'pages/vehicles/id/needsInHouseAdditionalProgramming'
import VehicleNeedsThirdPartyAdditionalProgrammingNEW from 'pages/vehicles/id/needsThirdPartyAdditionalProgramming'
import VehicleNeedsToBeMovedToStagingNEW from 'pages/vehicles/id/needsToBeMovedToStaging'
import VehicleWaitingOnThirdPartyAdditionalProgrammingNEW from 'pages/vehicles/id/waitingOnThirdPartyAdditionalProgramming'
import VehicleWaitingOnThirdPartyClusterConversionNEW from 'pages/vehicles/id/waitingOnThirdPartyClusterConversion'
import { Navigate, Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements, useLocation } from 'react-router-dom'

interface AuthorizedOutletProps {
  permissionSpecialIdentifiers: string[]
}

const AuthorizedOutlet = ({ permissionSpecialIdentifiers }: AuthorizedOutletProps) => {
  const { permissions, status, signOut } = useAuth()
  const location = useLocation()

  if (status === 'unauthorized') {
    return <Navigate to='/account/sign-in' state={{ from: location }} />
    // @ts-ignore
    // If a user does not have a permission in permissionSpecialIdentifiers, send them to /sign-in.
    // ts-ignore is used here to utilize a convenience with JavaScript. I believe this implementation is more readable and maintainable than
    // the TypeScript alternative.
    // Maxwell Sommer 09/09/2024
  } else if (!permissionSpecialIdentifiers.some(specialIdentifier => permissions[specialIdentifier] === true)) {
    signOut()
    return <Navigate to='/account/sign-in' /*state={{ from: location }}*/ />
  }

  return <Outlet />
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='account/sign-in' element={<SignIn />} />
      <Route path='account/forgot-password' element={<ForgotPassword />} />
      <Route path='account/reset-password' element={<ResetPassword />} />

      <Route element={<LayoutProvider />}>
        <Route path='*' element={<Blank />} />
        <Route element={<AuthorizedOutlet permissionSpecialIdentifiers={['COMPANIES_AND_PEOPLE_ADD_EDIT', 'COMPANIES_AND_PEOPLE_READ_ONLY']} />}>
          <Route path='/companies' element={<Companies />} />
          <Route path='/companies/:id' element={<CompaniesId />} />
          <Route path='/damage' element={<Damage />} />
          <Route path='/damage/add-damage' element={<AddDamage />} />
          <Route path='/damage/add-damage/:vehicleGuid' element={<AddDamage />} />
          <Route path='/titles/michigan-sos-bundles' element={<MichiganSOSBundlesListCard />} />
          <Route path='/titles/michigan-sos-bundles/:michiganSOSBundleGuid' element={<MichiganSOSBundlesDetails />} />
          <Route path='/michigan-sos-bundles/:michiganSOSBundleGuid/needs-to-be-taken-to-secretary-of-state' element={<NeedsToBeTakenToSecretaryOfState />} />
          <Route path='/people' element={<People />} />
          <Route path='/people/:id' element={<PeopleId />} />
        </Route>

        <Route path='/inbound-trips' element={<InboundTripsListCard />} />
        <Route path='/inbound-trips/:id' element={<InboundTripsDetails />} />
        <Route path='/inbound-trips/:inboundTripGuid/paps/:inboundTripPAPSGuid/manage-vehicles/' element={<InboundTripsGuidPAPSGuid />} />
        <Route path='/invoicing' element={<Invoicing />} />
        <Route path='/move-vehicle' element={<MoveVehicle />} />
        <Route path='/outbound-trips' element={<OutboundTripsListCard />} />
        <Route path='/outbound-trips/:outboundTripGuid' element={<OutboundTripsDetails />} />
        <Route path='/outbound-trips/:outboundTripGuid/needs-drivers-license-photo-uploaded' element={<OutboundTripNeedsDriversLicensePhotoUploadedNEW />} />
        <Route path='/reports' element={<Reports />} />

        <Route element={<AuthorizedOutlet permissionSpecialIdentifiers={['TITLES_ADD_EDIT']} />}>
          <Route path='/michigan-titles' element={<MichiganTitlesListCard />} />
          <Route path='/titles/michigan-sos-bundles' element={<InboundTripsListCard />} />
          <Route path='/michigan-outgoing-titles' element={<MichiganOutgoingTitlesListCard />} />
          <Route path='/michigan-outgoing-titles/:michiganOutgoingTitleGuid' element={<MichiganOutgoingTitlesId />} />

          <Route path='/titles/ohio-titles' element={<OhioTitlesListCard />} />

          <Route path='/titles/ohio-build-a-boxes' element={<OhioBuildABoxes />} />
          <Route path='/titles/ohio-build-a-boxes/:ohioBuildABoxGuid' element={<OhioBuildABox />} />

          <Route path='/ohio-outgoing-titles' element={<OhioOutgoingTitlesListCard />} />
          <Route path='/ohio-outgoing-titles/:ohioOutgoingTitleGuid' element={<OhioOutgoingTitlesId />} />
        </Route>

        <Route path='/cluster-shippers' element={<ClusterShippers />} />
        <Route path='/cluster-shippers/:clusterShipperGuid' element={<ClusterShippersId />} />
        <Route path='/settings' element={<FinalSettings />} />
        <Route path='/conformity' element={<Conformity />} />
        <Route path='/to-do-list' element={<ToDoList />} />
        <Route path='scan-cluster-barcode' element={<NeedsClusterRemovedScanClusterBarcodeIndex vehicle={new VehicleDto()} />} />
        <Route
          path='/cluster-shippers/:clusterShipperGuid/needs-cluster-delivered-to-third-party-conversion-company'
          element={<ClusterShipperNeedsClusterDeliveredToThirdPartyConversionCompany />}
        />
        <Route path='/vehicles/:vehicleGuid/waiting-on-third-party-cluster-conversion' element={<VehicleWaitingOnThirdPartyClusterConversionNEW />} />
        <Route path='/vehicles/:vehicleGuid/needs-cluster-conversion-in-house' element={<VehicleNeedsClusterConversionInHouseNEW />} />
        <Route path='/vehicles/:vehicleGuid/needs-in-house-additional-programming' element={<VehicleNeedsInHouseAdditionalProgrammingNEW />} />
        <Route path='/vehicles/:vehicleGuid/needs-third-party-additional-programming' element={<VehicleNeedsThirdPartyAdditionalProgrammingNEW />} />
        <Route path='/vehicles/:vehicleGuid/waiting-on-third-party-additional-programming' element={<VehicleWaitingOnThirdPartyAdditionalProgrammingNEW />} />
        <Route path='/vehicles/:vehicleGuid/cluster-reinstall' element={<VehicleClusterReinstallNEW />} />

        <Route path='/vehicles/:vehicleGuid/check-in' element={<VehicleCheckInNEW />} />

        <Route path='/vehicles/:vehicleGuid/cluster-removed' element={<VehicleClusterRemovedNEW />} />

        <Route path='/vehicles/:vehicleGuid/move-to-staging' element={<VehicleNeedsToBeMovedToStagingNEW />} />

        <Route path='/vehicles/:vehicleGuid/damage-details/:damageGuid' element={<DamageDetailsIndex />} />

        <Route path='/vehicles/:id/:tab?' element={<VehiclesId />} />

        <Route path='/ocr' element={<OCR />} />
        <Route path='/ocr/:documentGuid' element={<OCRDocumentGuidIndex />} />

        <Route path='/needs-to-be-moved-to-staging/:vehicleGuid' element={<NeedsToBeMovedToStaging />} />
        <Route path='/needs-drivers-license-uploaded/:outboundTripGuid' element={<NeedsDriversLicensePhotoUploaded />} />
      </Route>
    </>
  )
)

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ThemeProvider>
        <SnackbarProvider
          autoHideDuration={2000}
          action={snackbarId => (
            <IconButton
              color='inherit'
              onClick={() => {
                closeSnackbar(snackbarId)
              }}
              size='small'
            >
              <Clear fontSize='small' />
            </IconButton>
          )}
          maxSnack={6}
        >
          <AuthProvider>
            <FedExAddressProvider>
              <CssBaseline />
              <RouterProvider router={router} />
            </FedExAddressProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </LocalizationProvider>
  )
}

export default App
